import { useEffect, useRef, useState } from "react";
import Label from "./Label";
import { cx, prefersReducedMotion } from "./util";

/**
 * Framed console. lines: string | { t: "cmd" | "out" | "ok" | "hl" | "err", text }.
 * animate: types lines in, and only starts once the whole terminal would be in view. The box
 * grows as lines appear, so a hidden copy of the full body supplies the final height for that check.
 */
export default function Terminal({ title = "graspd", lines = [], status, animate = false, interval = 260, featured = false, caret = true, className, style }) {
  const ref = useRef(null), bodyRef = useRef(null), ghostRef = useRef(null);
  const [shown, setShown] = useState(animate && !prefersReducedMotion() ? 0 : lines.length);

  useEffect(() => {
    if (shown >= lines.length) return undefined;
    let timer, raf = 0, started = false;
    const go = () => {
      if (started) return;
      started = true;
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      let i = 0;
      timer = setInterval(() => { i += 1; setShown(i); if (i >= lines.length) clearInterval(timer); }, interval);
    };
    // Final height = current height with the body swapped for the full-size ghost body.
    const check = () => {
      raf = 0;
      const el = ref.current;
      if (!el || !bodyRef.current || !ghostRef.current) return go();
      const r = el.getBoundingClientRect(), vh = window.innerHeight;
      const finalH = r.height - bodyRef.current.offsetHeight + ghostRef.current.offsetHeight;
      // Taller than the screen can never fit: start once it fills the screen from the top.
      const ready = finalH > vh ? r.top <= vh * 0.05 && r.bottom > 0 : r.top >= 0 && r.top + finalH <= vh;
      if (ready) go();
    };
    function schedule() { if (!raf) raf = requestAnimationFrame(check); }
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    schedule();
    return () => {
      clearInterval(timer); cancelAnimationFrame(raf);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderLine = (l, i) => {
    const o = typeof l === "string" ? { t: "out", text: l } : l;
    return <span key={i} className={`gd-tl gd-tl-${o.t || "out"}`}>{o.text}</span>;
  };

  return (
    <div ref={ref} className={cx("gd-term", featured && "gd-term-featured", className)} style={style}>
      <div className="gd-term-bar">
        <b>{title}</b>
        <span className="gd-fill" aria-hidden="true">{"─".repeat(200)}</span>
        {status && <Label live active>{status}</Label>}
      </div>
      <div ref={bodyRef} className="gd-term-body">
        {lines.slice(0, shown).map(renderLine)}
        {caret && shown >= lines.length && <span className="gd-tl gd-tl-cmd"><span className="gd-caret" /></span>}
      </div>
      {animate && shown < lines.length && (
        <div ref={ghostRef} className="gd-term-body gd-term-ghost" aria-hidden="true">
          {lines.map(renderLine)}
          {caret && <span className="gd-tl gd-tl-cmd"><span className="gd-caret" /></span>}
        </div>
      )}
    </div>
  );
}
